// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A roster tile running attract (G1-12, M8-79; FR-024, 08 §2).
//
// Two modules do the thinking and this one does the wiring: `attractTiles.ts` decides which tiles
// animate and which ask for their art, `attractPainter.ts` draws them, and `attractArt.ts` reads a
// wire answer — all three without a browser and all three tested without one. What is here is an
// `IntersectionObserver`, a `MediaQueryList`, a visibility listener, one interval and one fetch —
// the five things that cannot be a pure function, kept together so that turning attract off is
// turning off one loop rather than auditing a component.
//
// **One loop for the whole grid, not one per tile.** The tiles register their canvases with the
// provider and the provider paints; a tile that owned its own timer would be N timers waking a
// phone up, and the cap would be a cap on nothing.
//
// **Off means off** (NFR-023, 06 §6). Under `prefers-reduced-motion` the loop is not started, the
// observer is not attached, the painter is not constructed and **nothing is fetched**. A grid of
// pulsing lamps is precisely the surface that setting exists for, and spending a player's data on
// pictures they have asked not to see would be answering a different request from the one they made.
//
// WHERE THE ART COMES FROM (M8-79)
// --------------------------------
// The bundled flagship carries its definition, and a tile drew nothing without one — which is every
// tile but that one. `GET /machines/{id}/versions/{semver}/art` answers the four fields a thumbnail
// needs, is **public where the definition route is gated**, and is 1.3-1.6 KB gzipped. So a tile is
// registered with either a picture in hand or the two strings needed to ask for one, and the
// provider does the asking — once per hash, for every tile the viewport says is on screen.
//
// The asking is the provider's rather than the tile's because the answer is shared: one painter
// holds the pictures, one place decides what has already been asked, and a tile that fetched for
// itself would ask again every time it scrolled back into view.
//
// **Every visible tile gets a picture; four of them move.** `MAX_LIVE_TILES` caps the frame loop,
// which is a cost that repeats sixty times a minute — not the pictures, which are a one-off 1.4 KB
// each and then cached for ever. `hold` paints a still frame the moment art arrives, so a tile
// outside the cap stands there lit rather than holding an empty box. A browser found the version
// that got this wrong.
//
// THE CONTEXT VALUE IS TWO CONTEXTS, AND THAT IS NOT TIDINESS
// ------------------------------------------------------------
// The first version put the functions and the live set in one object, and it **spun**: `liveTiles`
// returns a fresh array, so `setLive` always stored a new reference, which re-rendered the
// provider, which built a new context value, which re-ran every tile's effect, which called
// `offer`, which recomputed and set the live set again. A render loop with no exit.
//
// Nothing in the unit tests could see it — the decisions and the painting are both pure and both
// correct — and nothing in the browser *said* it either: no exception, no console error. What it
// looked like from outside was that **`/arcade` could no longer be navigated away from**. The URL
// changed and the page did not, because the main thread never got far enough to render the route
// it had moved to. `test/e2e/arcade.spec.ts` is what found it, on the assertion that a tile opens
// its machine.
//
// So: the functions live in a context that changes only when the *conditions* change, the live set
// lives in its own, and `setLive` compares before it stores. Any one of the three would have
// stopped the loop; all three are here because the reason each is needed is different. **The fetch
// set is kept in a ref and never in state**, for the same reason and with the same care: it changes
// on every scroll frame and nothing renders from it.

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  AttractPainter,
  ATTRACT_HEIGHT,
  ATTRACT_WIDTH,
  type AttractArt,
  type CabinetShotMaker,
} from '../attractPainter'
import {
  ATTRACT_INTERVAL_MS,
  attractIsWorthRunning,
  liveTiles,
  tilesToFetch,
  type AttractCandidate,
} from '../attractTiles'
import { parseAttractArt } from '../attractArt'
import { loadPicture, savePicture } from '../attractStore'
import { ART_PROJECTION } from '../../net/artProjection'
import { openAttractCache } from '../../storage/machineCache'
import { REDUCED_MOTION_QUERY, prefersReducedMotion } from '../../world/motionPreference'
// The same answer `MachineRuntime` asks for, from the module that owns it. A second reading of the
// player's flashing preference is a second chance for a tile and the machine beside it to disagree
// about a setting whose whole purpose is that it applies everywhere.
import { reduceFlashing } from '../accessibility'

/**
 * Where a tile's picture is, which is one of two places.
 *
 * The bundled machine has it. Every other published machine can be asked about, using the semver and
 * the content hash the roster already carries — which is what makes the request skippable on the
 * second visit without anybody being told the hash for that purpose.
 */
export interface TileArtSource {
  readonly machineId: string
  /** In hand, for the machine this build ships. */
  readonly definition?: AttractArt | undefined
  /** The version to ask about, when it is not. */
  readonly semver?: string | undefined
  /** The content hash, which is both the cache key and the `If-None-Match` the browser will send. */
  readonly definitionHash?: string | undefined
  /**
   * What to file this machine's finished picture under, when there is no definition hash (M8-85).
   *
   * **The bundled machine is the case.** It has no `definitionHash` on purpose — `tileFor` says a
   * made-up one "would be a string that looks like a hash" — and it is also the tile drawn earliest,
   * before the roster has answered, so waiting for one would mean the one machine that needs no
   * network is the last to appear. A separate field, with its own meaning and its own prefix, is
   * how it gets a stored picture without anything pretending to be a wire value.
   */
  readonly pictureKey?: string | undefined
}

/** Whether this tile could get art at all — see `AttractCandidate.fetchable`. */
const canAsk = (source: TileArtSource): boolean =>
  typeof source.semver === 'string' &&
  source.semver !== '' &&
  typeof source.definitionHash === 'string' &&
  source.definitionHash !== ''

/** Whether a tile has any way to a picture. A tile with none renders nothing at all. */
export const hasArtSource = (source: TileArtSource): boolean =>
  source.definition !== undefined || canAsk(source)

/**
 * How a slug's art is got, when it is not in hand.
 *
 * A parameter of the provider rather than an import inside it, so the tests that matter here — the
 * cap, the ordering, the once-per-hash rule — run with no network, no IndexedDB and no browser.
 */
export type ArtResolver = (machineId: string, semver: string, definitionHash: string) => Promise<AttractArt | undefined>

/**
 * Where one machine's art is filed.
 *
 * Exported so the rule can be asserted rather than read: the projection has to be *in* the key, and
 * it has to sit under `ART_PREFIX` so that `openAttractCache`'s eviction — which lists everything
 * beginning `art/` — can still see and clear the entries an older projection wrote.
 */
export function artCacheKey(definitionHash: string): string {
  return `${ART_PROJECTION}/${definitionHash}`
}

/**
 * The real one: the browser's own cache, then the server, then the cache again.
 *
 * Content-addressed, so the second visit costs nothing and a machine that has been republished
 * misses rather than needing a rule about when to distrust itself. The account module is imported
 * lazily for the reason `useMachine` imports it lazily: the front door must not pull the API client
 * into its first paint, and FR-030 says the front door talks to nobody.
 */
const overTheWire: ArtResolver = async (machineId, semver, definitionHash) => {
  try {
    const cache = await openAttractCache()
    // **Keyed on the projection as well as the hash** (M8-82). Content addressing is right about
    // the *definition* — a republish is a new hash and therefore a miss — and says nothing about
    // the **projection** of it this route answers with, which has changed twice under a stable
    // hash: M8-81 added the backglass and the flanks, M8-82 the mechanisms. Without this, a
    // returning visitor read their own IndexedDB, hit, and never made the request at all, so the
    // `art=` parameter on the URL could not save them either — the network was never reached.
    //
    // The projection is a **path segment under the same prefix**, deliberately, rather than a new
    // prefix: `openAttractCache` evicts by listing everything under `art/`, so yesterday's entries
    // stay visible to the cap and are cleared as new ones arrive. A new prefix would have stranded
    // them where nothing could ever see them again.
    const key = artCacheKey(definitionHash)
    const held = parseAttractArt(await cache.get(key))
    if (held !== undefined) return held
    const feed = await import('../account')
    const answer = await feed.fetchArt(machineId, semver)
    const parsed = parseAttractArt(answer)
    if (parsed === undefined) return undefined
    // Stored after it parses, never before. A cache of documents this build cannot draw is a cache
    // that misses on every read and costs a write each time.
    void cache.put(key, answer, machineId, semver)
    return parsed
  } catch {
    // A thumbnail. There is no second page to send anybody to and nothing on screen changes: the
    // tile goes on showing its name, which is what every tile did before this existed.
    return undefined
  }
}

/**
 * The cabinet renderer, loaded on demand and once (M8-81).
 *
 * **Dynamically imported, because it pulls three.js.** `/arcade` is a list of names and prices and
 * must not carry a 3-D renderer into its first paint; the front door already has one for the machine
 * it plays, and this is the same library arriving on the page that actually needs it. A failure to
 * load is not an error — it is a tile drawing its flat playfield, which is what every tile did
 * before this existed.
 */
let cabinet: { renderCabinetShot: CabinetShotMaker; forgetCabinetRenderer: () => void } | undefined
let cabinetLoading: Promise<void> | undefined

const loadCabinet = (): Promise<void> =>
  (cabinetLoading ??= import('../attractCabinet')
    .then((mod) => {
      cabinet = mod
    })
    .catch(() => undefined))

/** Handed to the painter at construction. Answers nothing until the module is in. */
const shotMaker: CabinetShotMaker = (art, width, height) =>
  cabinet?.renderCabinetShot(art, width, height)

interface AttractRegistry {
  /**
   * Register a tile's canvas and where its art is. Returns a function that unregisters.
   *
   * `onDrawable` is called when the painter holds a picture for this tile — immediately for the
   * bundled machine, and after a round trip for the rest. It replaces the `onScreenChanged`
   * callback that stood here and that **every caller passed `() => {}`**: on-screen is the
   * provider's business, and whether there is anything to fade in is the tile's.
   */
  readonly attach: (
    source: TileArtSource,
    canvas: HTMLCanvasElement,
    onDrawable: (drawable: boolean) => void,
  ) => () => void
}

const NOTHING: AttractRegistry = { attach: () => () => {} }

/**
 * The functions, which change only when the conditions do.
 *
 * **Separate from the live set on purpose.** A tile's effect depends on this; if the live set were
 * in here, every frame that changed which tiles animate would re-run every tile's effect, which
 * would change the live set. See the header.
 */
const RegistryContext = createContext<AttractRegistry>(NOTHING)

/** The slugs currently animating. Read by nothing that also registers, which is the point. */
const LiveContext = createContext<readonly string[]>([])

/** Whether two live sets are the same, so an equal one is never stored. */
const sameLive = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((slug, i) => slug === b[i])

/** One registered tile: its canvas, whether it is on screen, and where its art is. */
interface RegisteredTile {
  readonly canvas: HTMLCanvasElement
  readonly onScreen: boolean
  readonly source: TileArtSource
  readonly onDrawable: (drawable: boolean) => void
}

export interface AttractProviderProps {
  readonly children: React.ReactNode
  /** How art is got. Defaulted to the real one; supplied by tests. */
  readonly resolve?: ArtResolver | undefined
}

/**
 * The one loop, the one painter, the one observer and the one fetcher for a page of tiles.
 *
 * Mounted by the arcade around its grid. With no tile registered it does nothing at all — not a
 * paused loop, no loop — because a scheduler ticking over an empty set is a wake-up every 83 ms
 * that draws nothing and is invisible for exactly that reason.
 */
export function AttractProvider({ children, resolve }: AttractProviderProps): React.JSX.Element {
  // Read once at mount rather than per frame: a media query that changes mid-session re-runs the
  // effect below, which is the moment the answer actually has to change.
  const [reducedMotion, setReducedMotion] = useState(() => prefersReducedMotion())
  const [documentVisible, setDocumentVisible] = useState(
    () => globalThis.document?.visibilityState !== 'hidden',
  )
  const [live, setLive] = useState<readonly string[]>([])

  const painterRef = useRef<AttractPainter | undefined>(undefined)
  const tilesRef = useRef(new Map<string, RegisteredTile>())
  const orderRef = useRef<string[]>([])
  /**
   * The hashes already asked about, whatever the answer was.
   *
   * **By hash rather than by slug**, so a machine republished during a session is asked about again
   * and one scrolled past four times is not. Failures are recorded too: a refusal, an outage and an
   * unparseable answer are all permanent for this page, and retrying on the next scroll frame would
   * turn one dead tile into a request every 83 ms.
   */
  const askedRef = useRef(new Set<string>())
  // Held in a ref because it must not survive a resolver change and must not cause a render.
  const resolveRef = useRef<ArtResolver>(resolve ?? overTheWire)
  resolveRef.current = resolve ?? overTheWire

  useEffect(() => {
    const media = globalThis.matchMedia?.(REDUCED_MOTION_QUERY)
    if (media === undefined) return
    const listen = (): void => setReducedMotion(media.matches)
    media.addEventListener('change', listen)
    return () => media.removeEventListener('change', listen)
  }, [])

  useEffect(() => {
    const doc = globalThis.document
    if (doc === undefined) return
    const listen = (): void => setDocumentVisible(doc.visibilityState !== 'hidden')
    doc.addEventListener('visibilitychange', listen)
    return () => doc.removeEventListener('visibilitychange', listen)
  }, [])

  const registry = useMemo<AttractRegistry>(() => {
    const candidates = (): AttractCandidate[] => {
      const painter = painterRef.current
      return orderRef.current.map((slug) => {
        const tile = tilesRef.current.get(slug)
        return {
          slug,
          hasDefinition: painter !== undefined && painter.holds(slug),
          onScreen: tile?.onScreen === true,
          fetchable: tile !== undefined && canAsk(tile.source),
        }
      })
    }

    /**
     * Paint one machine's art into the shared surface, show it, and tell its tile.
     *
     * **A still frame is drawn immediately, before anything decides whether this tile animates.**
     * The cap is a limit on the frame loop, not on how many machines a player can see: four tiles
     * blink and the rest stand there lit, which is what a hall of real machines looks like and what
     * `tilesToFetch` is written to allow. Without this, a tile outside the cap would hold a picture
     * and show an empty box.
     */
    const hold = (slug: string, art: AttractArt): boolean => {
      if (reducedMotion) return false
      const painter = (painterRef.current ??= new AttractPainter(undefined, shotMaker))
      const ok = painter.prepare(slug, art)
      if (!ok) return false
      const tile = tilesRef.current.get(slug)
      if (tile === undefined) return false
      const ctx = tile.canvas.getContext('2d')
      // Step zero and the player's own flashing floor: the frame the loop would have drawn first.
      if (ctx !== null) painter.draw(ctx, slug, 0, reduceFlashing(), tile.canvas.width, tile.canvas.height)
      tile.onDrawable(true)
      return true
    }

    /**
     * The same, with the store in front of it (M8-85).
     *
     * **The render is the expensive thing in a roster and it was being redone every visit** — about
     * 90 ms a machine on a desktop GPU, several times that on a phone. So the finished picture is
     * looked for first, and only a machine nobody has drawn on this browser is drawn.
     *
     * Written **after** the tile is on screen, never before: the save is an encode and a database
     * write, and a player waiting on one to see a thumbnail they could already have been shown is
     * the cost this exists to remove, spent backwards. A store that will not read or will not
     * write answers `undefined` and `false`, and the whole thing degrades to what M8-84 did.
     */
    const holdStored = async (slug: string, art: AttractArt, hash: string | undefined): Promise<boolean> => {
      if (reducedMotion) return false
      const painter = (painterRef.current ??= new AttractPainter(undefined, shotMaker))
      if (hash !== undefined && !painter.holds(slug)) {
        const stored = await loadPicture(hash, ATTRACT_WIDTH, ATTRACT_HEIGHT)
        if (stored !== undefined) painter.adopt(slug, stored.image, stored.inserts)
      }
      // Asked before `hold`, because `hold` is what would draw it: this is "was it already here",
      // not "is it here now", and the two differ by exactly the render we are trying to avoid.
      const restored = painter.holds(slug)
      if (!hold(slug, art)) return false
      if (!restored && hash !== undefined) {
        const drawn = painter.pictureOf(slug)
        if (drawn !== undefined) void savePicture(hash, drawn.surface, drawn.inserts)
      }
      return true
    }

    const recompute = (): void => {
      const conditions = { reducedMotion, tier: 'low' as const, documentVisible }
      const next = liveTiles(candidates(), conditions)
      // Compared before it is stored. `liveTiles` returns a fresh array every call, so storing it
      // unconditionally makes every recompute a re-render — and every re-render a recompute.
      setLive((current) => (sameLive(current, next) ? current : next))

      for (const slug of tilesToFetch(candidates(), conditions)) {
        const tile = tilesRef.current.get(slug)
        const semver = tile?.source.semver
        const hash = tile?.source.definitionHash
        if (semver === undefined || hash === undefined) continue
        if (askedRef.current.has(hash)) continue
        askedRef.current.add(hash)
        void resolveRef.current(slug, semver, hash).then(async (art) => {
          // The picture and the renderer, both in hand before anything is painted.
          await loadCabinet()
          // Registered again between the ask and the answer? Then the tile scrolled away and back,
          // which is fine; gone altogether and `hold` finds no tile to tell, which is also fine.
          if (art !== undefined && (await holdStored(slug, art, hash))) recompute()
          // **A failure has to be said out loud**, or the tile keeps a reserved box for a picture
          // that is never coming. This is the state a build whose server does not answer the art
          // route yet is in, and it must look exactly like the roster did before M8-79.
          else tilesRef.current.get(slug)?.onDrawable(false)
        })
      }
    }

    return {
      attach: (source, canvas, onDrawable) => {
        const machineId = source.machineId
        tilesRef.current.set(machineId, { canvas, onScreen: false, source, onDrawable })
        if (!orderRef.current.includes(machineId)) orderRef.current.push(machineId)
        // In hand, but painted after the renderer is in: the bundled machine has no round trip to
        // wait for, and painting it before the cabinet module lands would give the one tile that
        // needs no network the one picture that is not the machine. A microtask, not a request.
        if (source.definition !== undefined) {
          const held = source.definition
          void loadCabinet().then(async () => {
            // The bundled machine goes through the store too. It has no round trip to wait for,
            // which makes it the tile whose render is most visible — it is the one drawn while the
            // page is still settling.
            if (await holdStored(machineId, held, source.pictureKey ?? source.definitionHash)) recompute()
          })
        }
        const observer = globalThis.IntersectionObserver === undefined
          ? undefined
          : new globalThis.IntersectionObserver((entries) => {
              for (const entry of entries) {
                const held_ = tilesRef.current.get(machineId)
                if (held_ === undefined) continue
                tilesRef.current.set(machineId, { ...held_, onScreen: entry.isIntersecting })
              }
              recompute()
            })
        observer?.observe(canvas)
        // No observer — an old browser, or a test — means every tile counts as on screen. The cap
        // still holds, so the worst case is the top four animating rather than the visible four.
        if (observer === undefined) {
          const held_ = tilesRef.current.get(machineId)
          if (held_ !== undefined) tilesRef.current.set(machineId, { ...held_, onScreen: true })
        }
        recompute()
        return () => {
          observer?.disconnect()
          tilesRef.current.delete(machineId)
          orderRef.current = orderRef.current.filter((slug) => slug !== machineId)
          recompute()
        }
      },
    }
  }, [reducedMotion, documentVisible])

  useEffect(() => {
    if (!attractIsWorthRunning(live)) return
    const painter = painterRef.current
    if (painter === undefined) return
    const floor = reduceFlashing()
    let step = 0
    const timer = globalThis.setInterval(() => {
      step += 1
      for (const slug of live) {
        const tile = tilesRef.current.get(slug)
        if (tile === undefined) continue
        const ctx = tile.canvas.getContext('2d')
        if (ctx === null) continue
        painter.draw(ctx, slug, step, floor, tile.canvas.width, tile.canvas.height)
      }
    }, ATTRACT_INTERVAL_MS)
    return () => globalThis.clearInterval(timer)
  }, [live])

  /**
   * Give the WebGL context back when the arcade goes away (M8-81).
   *
   * **The one renderer is the page's, not the module's.** It is shared for the life of this grid so
   * that a roster costs one context rather than one per tile — and a context that outlived the page
   * would be a context the front door's own machine has to compete for, on a browser that caps them
   * at about sixteen and evicts the oldest without asking. Leaving is the moment it is certainly
   * finished with.
   */
  useEffect(
    () => () => {
      cabinet?.forgetCabinetRenderer()
    },
    [],
  )

  return (
    <RegistryContext.Provider value={registry}>
      <LiveContext.Provider value={live}>{children}</LiveContext.Provider>
    </RegistryContext.Provider>
  )
}

export interface TileAttractProps {
  readonly machineId: string
  /** The art, when it is in hand. The bundled machine's is; nobody else's is. */
  readonly definition?: AttractArt | undefined
  /** The version to ask about, from the roster row. */
  readonly semver?: string | undefined
  /** The content hash, from the same row. */
  readonly definitionHash?: string | undefined
  /** Where this machine's finished picture is filed, when it has no hash. See `TileArtSource`. */
  readonly pictureKey?: string | undefined
  readonly title: string
}

/**
 * One tile's canvas.
 *
 * **Renders nothing at all for a tile with no way to a picture** — not a placeholder, and not a
 * still frame either: a grey rectangle beside a name says "an image failed to load", which is a
 * different and worse thing to say than nothing.
 *
 * A tile that *can* get one renders its canvas straight away, before the art arrives, and this is
 * not a placeholder either: `.tile__attract` is `opacity: 0` over its own aspect-ratio box, so the
 * space is reserved, nothing is drawn in it, and `--live` fades the picture in with no reflow. The
 * stylesheet was built that way for the bundled tile in G1-12 and turns out to be exactly what a
 * fetched one needs.
 */
export function TileAttract({
  machineId,
  definition,
  semver,
  definitionHash,
  pictureKey,
  title,
}: TileAttractProps): React.JSX.Element | null {
  const registry = useContext(RegistryContext)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  /**
   * Three states, not two.
   *
   * `waiting` reserves the box so the picture arrives without moving the page, `showing` fades it
   * in, and `none` takes the box back. The third is what a two-state version got wrong: a tile whose
   * art cannot be got — an old server, an outage, a machine whose art will not parse — kept an empty
   * rectangle above its name for ever, which is precisely the "an image failed to load" impression
   * this component has always refused to give.
   */
  const [state, setState] = useState<'waiting' | 'showing' | 'none'>('waiting')
  const drawable = hasArtSource({ machineId, definition, semver, definitionHash })

  useEffect(() => {
    if (!drawable) return
    const canvas = canvasRef.current
    if (canvas === null) return
    setState('waiting')
    return registry.attach({ machineId, definition, semver, definitionHash, pictureKey }, canvas, (ok) =>
      setState(ok ? 'showing' : 'none'),
    )
  }, [machineId, definition, semver, definitionHash, pictureKey, drawable, registry])

  if (!drawable || state === 'none') return null
  return (
    <canvas
      ref={canvasRef}
      className={state === 'showing' ? 'tile__attract tile__attract--live' : 'tile__attract'}
      // Decorative: the tile's own name and metadata already say what this is, and a canvas of a
      // playfield described to a screen reader would be a paragraph nobody asked for.
      aria-hidden="true"
      role="presentation"
      aria-label={title}
      width={ATTRACT_WIDTH}
      height={ATTRACT_HEIGHT}
    />
  )
}
